import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'motion/react';
import {
  EnvelopeSimple, CalendarCheck, CurrencyDollar, ArrowsClockwise,
  Star, Receipt, ArrowRight, ArrowLeft, Check, X,
  Lightning, Sparkle, Eye, Play, ChatText, CaretLeft, CaretRight,
  CircleNotch, CheckCircle, ToggleLeft, ToggleRight,
  MagnifyingGlass, XCircle, PlusCircle, ArrowDown, TreeStructure,
  GearSix, Globe, UsersThree, ListChecks, Megaphone, Bell, ClipboardText,
  UserPlus, Clock
} from '@phosphor-icons/react';
import { BACKEND_URL } from '../config';
import { fetchWithOrg } from '../utils/api';
import { useOrganization } from '../context/OrganizationContext';
import { getAppLogo } from '../utils/usedApps';
import { generateWorkflow, getToolRoleMapping } from '../utils/workflowGenerator';
import { inferIndustry } from '../utils/industryTools';
import StepSettingsPanel from '../components/StepSettingsPanel';

/* ─────────────────────────────────────────────── */
/*  CONSTANTS                                       */
/* ─────────────────────────────────────────────── */
const OUTCOMES = [
  {
    id: 'never-miss-lead',
    label: 'Never miss a lead',
    description: 'Respond instantly to every enquiry',
    icon: EnvelopeSimple,
    templateId: 'never_miss_a_lead_v1',
    category: 'Lead & Enquiries',
    color: '#10B981',
    toolTags: ['email', 'crm', 'messaging', 'sheets'],
  },
  {
    id: 'book-jobs',
    label: 'Book jobs automatically',
    description: 'Turn enquiries into confirmed bookings',
    icon: CalendarCheck,
    templateId: 'booking_assistant_v1',
    category: 'Bookings & Scheduling',
    color: '#60A5FA',
    toolTags: ['calendar', 'job_mgmt', 'email'],
  },
  {
    id: 'follow-up',
    label: 'Follow up every lead',
    description: 'Automatically chase and convert more work',
    icon: ArrowsClockwise,
    templateId: 'job_follow_up_assistant_v1',
    category: 'Customer Retention',
    color: '#A78BFA',
    toolTags: ['email', 'crm', 'marketing'],
  },
  {
    id: 'after-hours',
    label: 'After-hours response',
    description: 'Handle enquiries outside business hours automatically',
    icon: Clock,
    templateId: 'missed_call_recovery_v1',
    category: 'Lead & Enquiries',
    color: '#3B82F6',
    toolTags: ['email', 'messaging', 'crm'],
  },
  {
    id: 'send-invoices',
    label: 'Send invoices automatically',
    description: 'Generate and send invoices after jobs complete',
    icon: Receipt,
    templateId: 'send_invoices_v1',
    category: 'Billing & Invoicing',
    color: '#F59E0B',
    toolTags: ['accounting', 'job_mgmt', 'email'],
  },
  {
    id: 'appointment-reminders',
    label: 'Appointment reminders',
    description: 'Reduce no-shows with automated reminders',
    icon: Bell,
    templateId: 'appointment_reminders_v1',
    category: 'Bookings & Scheduling',
    color: '#EC4899',
    toolTags: ['calendar', 'messaging', 'email'],
  },
  {
    id: 'website-enquiries',
    label: 'Capture website enquiries',
    description: 'Turn website visitors into real jobs',
    icon: Globe,
    templateId: 'lead_qualification_v1',
    category: 'Website & Leads',
    color: '#10B981',
    toolTags: ['sheets', 'crm', 'email'],
  },
  {
    id: 'review-collection',
    label: 'Collect reviews after jobs',
    description: 'Automatically ask happy customers for reviews',
    icon: Star,
    templateId: 'upsell_cross_sell_v1',
    category: 'Reputation',
    color: '#FBBF24',
    toolTags: ['crm', 'email', 'job_mgmt', 'messaging'],
  },
  {
    id: 'reactivate',
    label: 'Reactivate old customers',
    description: 'Win back past customers automatically',
    icon: UsersThree,
    templateId: 'rebook_past_customers_v1',
    category: 'Customer Retention',
    color: '#F59E0B',
    toolTags: ['marketing', 'crm', 'email'],
  },
  {
    id: 'customer-onboarding',
    label: 'Onboard new customers',
    description: 'Welcome packs, intro emails, and setup tasks',
    icon: UserPlus,
    templateId: 'never_miss_a_lead_v1',
    category: 'Customer Success',
    color: '#8B5CF6',
    toolTags: ['email', 'crm', 'sheets', 'project_mgmt'],
  },
  {
    id: 'job-updates',
    label: 'Job status updates',
    description: 'Keep customers informed on job progress',
    icon: ClipboardText,
    templateId: 'job_follow_up_assistant_v1',
    category: 'Organisation & Comms',
    color: '#06B6D4',
    toolTags: ['job_mgmt', 'messaging', 'email'],
  },
  {
    id: 'stay-organised',
    label: 'Stay organised (jobs & comms)',
    description: 'Keep jobs, messages, and updates in sync',
    icon: ListChecks,
    templateId: 'internal_admin_assistant_v1',
    category: 'Organisation & Comms',
    color: '#FACC15',
    toolTags: ['sheets', 'project_mgmt', 'calendar', 'messaging'],
  },
  {
    id: 'chase-invoices',
    label: 'Chase unpaid invoices',
    description: 'Automatically follow up on overdue payments',
    icon: CurrencyDollar,
    templateId: 'chase_unpaid_invoices_v1',
    category: 'Billing & Invoicing',
    color: '#EF4444',
    toolTags: ['accounting', 'email', 'crm'],
  },
  {
    id: 'seasonal-promos',
    label: 'Run seasonal promotions',
    description: 'Automated campaigns for holidays and peak seasons',
    icon: Megaphone,
    templateId: 'seasonal_promotions_v1',
    category: 'Marketing',
    color: '#F97316',
    toolTags: ['marketing', 'email', 'crm'],
  },
];

// Maps tool names → tags for relevance scoring
const TOOL_TAG_MAP = {
  // Email
  'Gmail': 'email', 'Outlook': 'email', 'SendGrid': 'email',
  // Calendar
  'Google Calendar': 'calendar', 'Calendly': 'calendar',
  // CRM
  'HubSpot': 'crm', 'Salesforce': 'crm', 'Pipedrive': 'crm', 'Copper': 'crm', 'Zoho': 'crm',
  // Accounting
  'Xero': 'accounting', 'QuickBooks': 'accounting', 'MYOB': 'accounting', 'Stripe': 'accounting',
  // Job management
  'ServiceM8': 'job_mgmt', 'Jobber': 'job_mgmt', 'Tradify': 'job_mgmt', 'Fergus': 'job_mgmt',
  'ServiceTitan': 'job_mgmt', 'simPRO': 'job_mgmt', 'Housecall Pro': 'job_mgmt', 'FieldPulse': 'job_mgmt',
  'ZenMaid': 'job_mgmt', 'AccuLynx': 'job_mgmt', 'JobNimbus': 'job_mgmt',
  // Messaging
  'Slack': 'messaging', 'WhatsApp': 'messaging', 'Microsoft Teams': 'messaging', 'Twilio': 'messaging', 'Discord': 'messaging',
  // Sheets/Data
  'Google Sheets': 'sheets', 'Airtable': 'sheets',
  // Marketing
  'Mailchimp': 'marketing', 'ActiveCampaign': 'marketing', 'Constant Contact': 'marketing',
  // Project management
  'Asana': 'project_mgmt', 'Trello': 'project_mgmt', 'Monday.com': 'project_mgmt', 'Notion': 'project_mgmt',
  'ClickUp': 'project_mgmt', 'Jira': 'project_mgmt', 'Linear': 'project_mgmt', 'Basecamp': 'project_mgmt',
  // Other
  'Google Drive': 'storage', 'Dropbox': 'storage', 'Shopify': 'ecommerce',
  'Zendesk': 'crm', 'Freshdesk': 'crm', 'Intercom': 'crm',
  'Google Business Profile': 'crm', 'Trustpilot': 'crm',
  'Facebook': 'marketing', 'Instagram': 'marketing',
  'Zapier': 'automation', 'PandaDoc': 'documents', 'Typeform': 'sheets',
};

// Score outcomes by tool relevance — returns sorted outcomes
const rankOutcomesByTools = (selectedTools) => {
  const tags = new Set();
  selectedTools.forEach(t => {
    const tag = TOOL_TAG_MAP[t];
    if (tag) tags.add(tag);
  });

  return OUTCOMES.map(o => {
    const score = (o.toolTags || []).filter(t => tags.has(t)).length;
    return { ...o, relevanceScore: score };
  }).sort((a, b) => b.relevanceScore - a.relevanceScore);
};

const SETUP_MESSAGES = [
  'Understanding your business...',
  'Building your workflow...',
  'Connecting your tools...',
  'Almost ready...',
];

// Outcome-contextual tool suggestions — shown when user hasn't added all relevant tools
const OUTCOME_SUGGESTED_TOOLS = {
  'never-miss-lead': ['Gmail', 'Outlook', 'HubSpot', 'Slack', 'Google Sheets', 'WhatsApp'],
  'book-jobs': ['Google Calendar', 'Calendly', 'Outlook', 'Gmail', 'ServiceM8', 'Jobber'],
  'follow-up': ['Gmail', 'Outlook', 'HubSpot', 'Mailchimp', 'Pipedrive', 'Slack'],
  'after-hours': ['Gmail', 'Outlook', 'Slack', 'WhatsApp', 'HubSpot'],
  'send-invoices': ['Xero', 'QuickBooks', 'Stripe', 'Gmail', 'ServiceM8'],
  'appointment-reminders': ['Google Calendar', 'Calendly', 'Gmail', 'WhatsApp', 'Slack'],
  'website-enquiries': ['Gmail', 'HubSpot', 'Google Sheets', 'Slack', 'Airtable', 'Zapier'],
  'review-collection': ['Gmail', 'Google Business Profile', 'Trustpilot', 'HubSpot'],
  'reactivate': ['Mailchimp', 'Gmail', 'HubSpot', 'Google Sheets', 'Stripe'],
  'customer-onboarding': ['Gmail', 'HubSpot', 'Google Sheets', 'Slack', 'Notion'],
  'job-updates': ['ServiceM8', 'Jobber', 'Gmail', 'Slack', 'WhatsApp'],
  'stay-organised': ['Google Calendar', 'Slack', 'Google Sheets', 'Airtable', 'Google Drive', 'Notion'],
  'chase-invoices': ['Xero', 'QuickBooks', 'Gmail', 'Stripe', 'MYOB'],
  'seasonal-promos': ['Mailchimp', 'Gmail', 'HubSpot', 'Facebook', 'Instagram'],
};

// Filter out test/dev artifacts from tool names
const isTestTool = (name) => /^test[_\s]/i.test(name) || /test$/i.test(name) || name.includes('TEST_');

// Local app catalog for instant search results (before API responds)
const LOCAL_APP_CATALOG = [
  'Gmail', 'Outlook', 'Google Calendar', 'Calendly', 'Slack', 'Google Sheets',
  'Google Drive', 'HubSpot', 'Salesforce', 'Pipedrive', 'Xero', 'QuickBooks',
  'Stripe', 'Mailchimp', 'Jobber', 'ServiceM8', 'Housecall Pro', 'Airtable',
  'WhatsApp', 'Twilio', 'Zoom', 'Notion', 'Asana', 'Trello', 'Jira',
  'Shopify', 'Zendesk', 'Freshdesk', 'Intercom', 'Discord', 'Microsoft Teams',
  'Dropbox', 'Linear', 'GitHub', 'Google Business Profile', 'Trustpilot',
  'Facebook', 'Instagram', 'Zapier', 'PandaDoc', 'MYOB', 'Tradify',
  'Fergus', 'ServiceTitan', 'simPRO', 'FieldPulse', 'ZenMaid', 'AccuLynx',
  'JobNimbus', 'Zoho', 'ActiveCampaign', 'Constant Contact', 'SendGrid',
  'Typeform', 'Monday.com', 'ClickUp', 'Basecamp', 'Copper',
];

const TOTAL_STEPS = 5;

/* ─────────────────────────────────────────────── */
/*  STEP INDICATOR                                  */
/* ─────────────────────────────────────────────── */
const StepIndicator = ({ current, total }) => (
  <div className="flex items-center gap-2" data-testid="step-indicator">
    {Array.from({ length: total }, (_, i) => (
      <div key={i} className="h-1 rounded-full transition-all duration-300"
        style={{ width: i + 1 === current ? 32 : 16, backgroundColor: i + 1 <= current ? '#00E5C0' : 'rgba(255,255,255,0.08)' }} />
    ))}
  </div>
);

/* ─────────────────────────────────────────────── */
/*  APP ICON (reusable)                             */
/* ─────────────────────────────────────────────── */
const AppIcon = ({ name, size = 28, className = '' }) => (
  <img src={getAppLogo(name)} alt={name} className={`rounded-md object-contain ${className}`}
    style={{ width: size, height: size }}
    onError={(e) => { e.target.src = '/app-icons/generic-app.svg'; e.target.onerror = null; }} />
);

/* ─────────────────────────────────────────────── */
/*  STEP 1 — YOUR TOOLS (moved from Step 2)         */
/* ─────────────────────────────────────────────── */
const ToolsFirstStep = ({ suggested, selected, searchQuery, searchResults, searchLoading, totalApps, onToggle, onSearchChange, onContinue, detectedIndustry }) => {
  const unselectedSuggested = suggested.filter(t => !selected.has(t));
  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.3 }} className="w-full">
      <h1 className="text-2xl sm:text-3xl font-bold text-[#F0F4F8] mb-2 tracking-tight" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
        Confirm your tools
      </h1>
      <p className="text-[#7D8896] text-sm mb-6">We'll suggest the best workflows based on what you use.</p>

      {/* Industry detection hint */}
      {detectedIndustry && (
        <div className="mb-5 flex items-center gap-2 px-3 py-2 rounded-lg bg-[rgba(0,229,192,0.03)] border border-[rgba(0,229,192,0.1)]" data-testid="industry-hint">
          <Sparkle size={14} className="text-[#00E5C0] shrink-0" weight="fill" />
          <p className="text-xs text-[#7D8896]">
            Suggestions tailored for <span className="text-[#00E5C0] font-medium">{detectedIndustry}</span>
          </p>
        </div>
      )}

      {/* YOUR TOOLS (primary) */}
      <p className="text-xs text-[#F0F4F8] font-semibold uppercase tracking-[0.08em] mb-3">Your tools</p>
      {selected.size > 0 ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5 mb-7" data-testid="selected-tools-pills">
          {[...selected].map((tool) => (
            <button key={tool} data-testid={`tool-selected-${tool.toLowerCase().replace(/\s+/g, '-')}`} onClick={() => onToggle(tool)}
              className="group flex items-center gap-3 rounded-xl border border-[rgba(0,229,192,0.25)] bg-[rgba(0,229,192,0.04)] px-3.5 py-3 transition-all hover:bg-[rgba(0,229,192,0.08)] shadow-[0_0_16px_rgba(0,229,192,0.04)]">
              <AppIcon name={tool} size={28} />
              <span className="text-sm font-medium text-[#F0F4F8] flex-1 text-left truncate">{tool}</span>
              <XCircle size={16} weight="fill" className="text-[#5C6778] group-hover:text-red-400 shrink-0 transition-colors" />
            </button>
          ))}
        </div>
      ) : (
        <div className="rounded-xl border border-dashed border-[rgba(255,255,255,0.10)] bg-[rgba(255,255,255,0.015)] px-4 py-5 text-center mb-7 transition-colors hover:border-[rgba(255,255,255,0.15)] hover:bg-[rgba(255,255,255,0.025)]">
          <p className="text-xs text-[#5A6370]">Pick a few tools below to get started</p>
        </div>
      )}

      {/* SUGGESTED ADDITIONS */}
      {unselectedSuggested.length > 0 && (
        <>
          <p className="text-xs text-[#7D8896] font-medium uppercase tracking-[0.08em] mb-2.5">Add more tools</p>
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 mb-6">
            {unselectedSuggested.map((tool) => (
              <button key={tool} data-testid={`tool-suggest-${tool.toLowerCase().replace(/\s+/g, '-')}`} onClick={() => onToggle(tool)}
                className="flex flex-col items-center gap-1.5 rounded-lg border border-[rgba(255,255,255,0.08)] bg-[#1F2937] p-2.5 transition-all hover:border-[rgba(255,255,255,0.12)] hover:bg-[rgba(255,255,255,0.02)]">
                <AppIcon name={tool} size={22} className="opacity-60" />
                <span className="text-[10px] text-[#7D8896] text-center leading-tight">{tool}</span>
              </button>
            ))}
          </div>
        </>
      )}

      {/* SEARCH */}
      <div className="relative mb-3">
        <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5C6778]" />
        <input
          data-testid="tool-search-input"
          type="text" value={searchQuery} onChange={(e) => onSearchChange(e.target.value)}
          placeholder="Search 1,000+ apps (Gmail, Xero, ServiceM8, HubSpot...)"
          className="w-full pl-8 pr-3 py-2.5 rounded-lg bg-[#1F2937] border border-[rgba(255,255,255,0.08)] text-sm text-[#F0F4F8] placeholder:text-[#5C6778] outline-none focus:border-[rgba(0,229,192,0.3)] transition-colors"
        />
        {searchLoading && (
          <CircleNotch size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#5C6778] animate-spin" />
        )}
      </div>
      {searchQuery && searchResults.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-6 max-h-48 overflow-y-auto" data-testid="tool-search-results">
          {searchResults.map((app) => {
            const isSelected = selected.has(app.name);
            return (
              <button key={app.slug || app.name} onClick={() => onToggle(app.name)}
                data-testid={`tool-result-${(app.slug || app.name).toLowerCase().replace(/\s+/g, '-')}`}
                className={`flex items-center gap-2 rounded-lg border px-3 py-2.5 text-xs transition-all ${
                  isSelected
                    ? 'border-[rgba(0,229,192,0.25)] bg-[rgba(0,229,192,0.04)] text-[#F0F4F8]'
                    : 'border-[rgba(255,255,255,0.08)] bg-[#1F2937] text-[#7D8896] hover:border-[rgba(255,255,255,0.12)]'
                }`}>
                <img src={app.logo} alt={app.name} className="w-5 h-5 rounded object-contain shrink-0"
                  onError={(e) => { e.target.src = '/app-icons/generic-app.svg'; e.target.onerror = null; }} />
                <span className="flex-1 text-left truncate">{app.name}</span>
                {isSelected ? <Check size={10} className="text-[#00E5C0] shrink-0" /> : <PlusCircle size={12} className="text-[#5C6778] shrink-0" />}
              </button>
            );
          })}
        </div>
      )}
      {searchQuery && !searchLoading && searchResults.length === 0 && (
        <p className="text-xs text-[#5C6778] mb-6">No apps matching "{searchQuery}" — try a different name</p>
      )}

      {/* Integration ecosystem hint */}
      <div className="rounded-lg border border-[rgba(255,255,255,0.04)] bg-[#1F2937] px-3.5 py-2.5 mb-4" data-testid="integration-ecosystem-hint">
        <p className="text-[10px] text-[#5C6778] leading-relaxed">
          {totalApps > 0 ? (
            <>Search across <span className="text-[#7D8896] font-medium">{totalApps.toLocaleString()}+ apps</span> — connect any tool your business runs on.</>
          ) : (
            <>Connect any of <span className="text-[#7D8896] font-medium">1,000+ apps</span> your business runs on.</>
          )}
        </p>
      </div>

      <div className="flex items-center justify-end pt-2">
        <button onClick={onContinue} data-testid="tools-continue-btn"
          disabled={selected.size === 0}
          className="px-5 py-2.5 rounded-lg bg-[#00E5C0] hover:bg-[#00D4B0] text-[#0B0F14] text-sm font-semibold transition-colors flex items-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed">
          See what we can build <ArrowRight size={14} />
        </button>
      </div>
    </motion.div>
  );
};

/* ─────────────────────────────────────────────── */
/*  STEP 2 — SMART OUTCOME SELECTION (ranked)       */
/* ─────────────────────────────────────────────── */
const SmartOutcomeStep = ({ selectedTools, onSelect, onCustom, onBack }) => {
  const ranked = rankOutcomesByTools(selectedTools);
  const recommended = ranked.filter(o => o.relevanceScore > 0);
  const other = ranked.filter(o => o.relevanceScore === 0);

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.3 }} className="w-full">
      <h1 className="text-2xl sm:text-3xl font-bold text-[#F0F4F8] mb-2 tracking-tight" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
        What do you want help with?
      </h1>
      <p className="text-[#7D8896] text-sm mb-6">Based on your tools, here's what we recommend.</p>

      {/* Recommended outcomes */}
      {recommended.length > 0 && (
        <>
          <div className="flex items-center gap-2 mb-3">
            <Sparkle size={14} className="text-[#00E5C0]" weight="fill" />
            <p className="text-xs text-[#00E5C0] font-semibold uppercase tracking-[0.08em]">Recommended for your tools</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
            {recommended.map((o) => {
              const Icon = o.icon;
              return (
                <button key={o.id} data-testid={`outcome-${o.id}`} onClick={() => onSelect(o)}
                  className="group text-left rounded-xl border border-[rgba(0,229,192,0.12)] bg-[rgba(0,229,192,0.02)] p-4 sm:p-5 transition-all duration-200 hover:border-[rgba(0,229,192,0.3)] hover:bg-[rgba(0,229,192,0.05)] hover:-translate-y-0.5 hover:shadow-[0_8px_32px_rgba(0,229,192,0.06)]">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ backgroundColor: `${o.color}15` }}>
                      <Icon size={20} style={{ color: o.color }} weight="duotone" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-[#F0F4F8] font-semibold text-sm leading-snug mb-1 group-hover:text-[#00E5C0] transition-colors">{o.label}</p>
                      <p className="text-[#7D8896] text-xs leading-relaxed">{o.description}</p>
                    </div>
                    <ArrowRight size={14} className="text-[#5C6778] mt-0.5 opacity-0 group-hover:opacity-100 group-hover:text-[#00E5C0] transition-all shrink-0" />
                  </div>
                </button>
              );
            })}
          </div>
        </>
      )}

      {/* Other outcomes */}
      {other.length > 0 && (
        <>
          <div className="flex items-center gap-3 mb-3">
            <div className="flex-1 h-px bg-[rgba(255,255,255,0.06)]" />
            <span className="text-[10px] text-[#5C6778] uppercase tracking-widest font-medium">More options</span>
            <div className="flex-1 h-px bg-[rgba(255,255,255,0.06)]" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
            {other.map((o) => {
              const Icon = o.icon;
              return (
                <button key={o.id} data-testid={`outcome-${o.id}`} onClick={() => onSelect(o)}
                  className="group text-left rounded-xl border border-[rgba(255,255,255,0.08)] bg-[#1F2937] p-4 sm:p-5 transition-all duration-200 hover:border-[rgba(0,229,192,0.3)] hover:bg-[rgba(0,229,192,0.03)] hover:-translate-y-0.5">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ backgroundColor: `${o.color}15` }}>
                      <Icon size={20} style={{ color: o.color }} weight="duotone" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-[#F0F4F8] font-semibold text-sm leading-snug mb-1 group-hover:text-[#00E5C0] transition-colors">{o.label}</p>
                      <p className="text-[#7D8896] text-xs leading-relaxed">{o.description}</p>
                    </div>
                    <ArrowRight size={14} className="text-[#5C6778] mt-0.5 opacity-0 group-hover:opacity-100 group-hover:text-[#00E5C0] transition-all shrink-0" />
                  </div>
                </button>
              );
            })}
          </div>
        </>
      )}

      <div className="flex items-center justify-between pt-2">
        <button onClick={onBack} data-testid="outcome-back-btn" className="flex items-center gap-1.5 text-sm text-[#7D8896] hover:text-[#F0F4F8] transition-colors">
          <CaretLeft size={14} /> Back to tools
        </button>
        <button data-testid="build-custom-btn" onClick={onCustom}
          className="group flex items-center gap-2 px-4 py-2.5 rounded-xl border border-[rgba(255,255,255,0.08)] bg-[#1F2937] hover:border-[rgba(255,255,255,0.15)] hover:bg-[rgba(255,255,255,0.03)] transition-all">
          <ChatText size={16} className="text-[#7D8896] group-hover:text-[#F0F4F8] transition-colors" />
          <span className="text-xs text-[#7D8896] group-hover:text-[#F0F4F8] transition-colors">Build something custom</span>
        </button>
      </div>
    </motion.div>
  );
};

/* ─────────────────────────────────────────────── */
/*  STEP 2.5 — TOOL CONFIRMATION (animated)         */
/* ─────────────────────────────────────────────── */
const ToolConfirmationStep = ({ toolMapping, onDone }) => {
  const [visibleCount, setVisibleCount] = useState(0);
  const [complete, setComplete] = useState(false);

  useEffect(() => {
    if (visibleCount < toolMapping.length) {
      const t = setTimeout(() => setVisibleCount(v => v + 1), 350);
      return () => clearTimeout(t);
    } else {
      const t = setTimeout(() => setComplete(true), 600);
      return () => clearTimeout(t);
    }
  }, [visibleCount, toolMapping.length]);

  useEffect(() => {
    if (complete) {
      const t = setTimeout(onDone, 400);
      return () => clearTimeout(t);
    }
  }, [complete, onDone]);

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0, y: -10 }}
      transition={{ duration: 0.3 }} className="w-full flex flex-col items-center py-12" data-testid="tool-confirmation-step">
      <div className="relative mb-6">
        <div className="w-14 h-14 rounded-2xl bg-[rgba(0,229,192,0.08)] flex items-center justify-center">
          <Sparkle size={28} className="text-[#00E5C0]" weight="fill" />
        </div>
        <motion.div className="absolute -inset-2 rounded-2xl border border-[rgba(0,229,192,0.15)]"
          animate={{ scale: [1, 1.1, 1], opacity: [0.4, 0, 0.4] }}
          transition={{ duration: 1.8, repeat: Infinity }} />
      </div>
      <h2 className="text-xl font-bold text-[#F0F4F8] mb-2 text-center" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
        Setting this up using your tools
      </h2>
      <p className="text-sm text-[#7D8896] mb-8 text-center">Building a workflow tailored to your stack.</p>

      <div className="w-full max-w-sm space-y-2.5" data-testid="tool-role-mapping">
        {toolMapping.map((item, i) => (
          <motion.div
            key={`${item.tool}-${i}`}
            initial={{ opacity: 0, x: -16 }}
            animate={i < visibleCount ? { opacity: 1, x: 0 } : { opacity: 0, x: -16 }}
            transition={{ duration: 0.25, ease: 'easeOut' }}
            className="flex items-center gap-3 px-4 py-3 rounded-xl bg-[#1F2937] border border-[rgba(255,255,255,0.08)]"
            data-testid={`tool-role-${item.tool.toLowerCase().replace(/\s+/g, '-')}`}
          >
            <AppIcon name={item.tool} size={24} />
            <span className="text-sm text-[#F0F4F8] font-medium flex-1">{item.role}</span>
            <span className="text-xs text-[#00E5C0] font-medium">{item.tool}</span>
            {i < visibleCount && (
              <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: 'spring', stiffness: 300 }}>
                <Check size={14} className="text-[#00E5C0]" weight="bold" />
              </motion.div>
            )}
          </motion.div>
        ))}
      </div>

      {complete && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="mt-6 flex items-center gap-2 text-[#00E5C0] text-sm font-medium">
          <CheckCircle size={16} weight="fill" /> All tools mapped
        </motion.div>
      )}
    </motion.div>
  );
};

/* ─────────────────────────────────────────────── */
/*  STEP 3 — WORKFLOW PREVIEW (Dynamic)             */
/* ─────────────────────────────────────────────── */
const STEP_COLORS = {
  trigger: { bg: 'rgba(0,229,192,0.06)', border: 'rgba(0,229,192,0.2)', text: '#00E5C0', dot: '#00E5C0' },
  action:  { bg: 'rgba(99,102,241,0.06)', border: 'rgba(99,102,241,0.2)', text: '#818CF8', dot: '#818CF8' },
};

const WorkflowPreviewStep = ({ outcome, workflowSteps, selectedApps, onActivate, onBack, onRemoveStep, onUpdateStep, connectedApps }) => {
  const [selectedStepIndex, setSelectedStepIndex] = useState(null);
  const selectedStep = selectedStepIndex !== null ? workflowSteps[selectedStepIndex] : null;

  const handleStepUpdate = useCallback((updated) => {
    if (onUpdateStep && selectedStepIndex !== null) {
      onUpdateStep(selectedStepIndex, updated);
    }
  }, [onUpdateStep, selectedStepIndex]);

  return (
  <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }}
    transition={{ duration: 0.3 }} className="w-full">
    <div className="flex items-center gap-2 mb-2">
      <TreeStructure size={16} className="text-[#00E5C0]" weight="duotone" />
      <p className="text-xs text-[#00E5C0] font-medium uppercase tracking-[0.08em]">Built for your business</p>
    </div>
    <h1 className="text-2xl sm:text-3xl font-bold text-[#F0F4F8] mb-2 tracking-tight" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
      Here's your workflow
    </h1>
    <p className="text-[#7D8896] text-sm mb-6">
      This workflow uses <span className="text-[#F0F4F8] font-medium">{Array.from(selectedApps).join(', ')}</span> to {outcome?.label?.toLowerCase() || 'automate your business'}.
    </p>

    <div className="flex gap-4">
      {/* Workflow Steps — Visual Flow */}
      <div className={`space-y-0 mb-6 ${selectedStep ? 'flex-1' : 'w-full'}`} data-testid="workflow-preview">
        {workflowSteps.map((step, i) => {
          const colors = STEP_COLORS[step.type] || STEP_COLORS.action;
          const isLast = i === workflowSteps.length - 1;
          const isSelected = selectedStepIndex === i;
          const isConfigurable = step.provider && step.provider !== 'platform';
          return (
            <div key={step.id || i}>
              <motion.div
                initial={{ opacity: 0, x: -12 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.08 }}
                data-testid={`workflow-step-${i}`}
                onClick={() => isConfigurable && setSelectedStepIndex(isSelected ? null : i)}
                className={`flex items-center gap-3 p-3.5 rounded-xl border transition-colors ${isConfigurable ? 'cursor-pointer hover:border-[rgba(0,229,192,0.25)]' : ''} ${isSelected ? 'ring-1 ring-[rgba(0,229,192,0.3)]' : ''}`}
                style={{ backgroundColor: colors.bg, borderColor: isSelected ? 'rgba(0,229,192,0.3)' : colors.border, boxShadow: '0 2px 6px rgba(0,0,0,0.2)' }}
              >
                {/* Step number */}
                <div className="w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-bold shrink-0"
                  style={{ color: colors.text, backgroundColor: 'rgba(255,255,255,0.06)' }}>
                  {i + 1}
                </div>

                {/* App icon if present */}
                {step.app && (
                  <AppIcon name={step.app} size={22} className="shrink-0" />
                )}
                {!step.app && (
                  <div className="w-[22px] h-[22px] rounded flex items-center justify-center shrink-0"
                    style={{ backgroundColor: `${colors.text}20` }}>
                    <Sparkle size={12} style={{ color: colors.text }} weight="fill" />
                  </div>
                )}

                {/* Label */}
                <span className="text-sm font-medium flex-1" style={{ color: colors.text }}>{step.label}</span>

                {/* Type badge */}
                <span className="text-[9px] uppercase tracking-[0.08em] text-[#7D8896] shrink-0">
                  {step.type === 'trigger' ? 'input' : step.app ? 'app' : 'ai'}
                </span>

                {/* Configure indicator */}
                {isConfigurable && (
                  <GearSix size={12} className="text-[#5C6778] shrink-0" />
                )}
              </motion.div>

              {/* Connector line */}
              {!isLast && (
                <div className="flex justify-center py-0.5">
                  <div className="w-px h-4 bg-[rgba(255,255,255,0.12)]" />
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Step Settings Panel (appears to the right) */}
      <StepSettingsPanel
        step={selectedStep}
        isOpen={selectedStep !== null}
        onClose={() => setSelectedStepIndex(null)}
        onUpdate={handleStepUpdate}
        connectedApps={connectedApps}
      />
    </div>

    {/* App coverage confirmation */}
    <div className="rounded-xl border border-[rgba(0,229,192,0.1)] bg-[rgba(0,229,192,0.02)] px-4 py-3 mb-6 flex items-center gap-3" data-testid="app-coverage-badge">
      <CheckCircle size={16} weight="fill" className="text-[#00E5C0] shrink-0" />
      <p className="text-xs text-[#7D8896]">
        All {selectedApps.size} selected tool{selectedApps.size !== 1 ? 's' : ''} are included in this workflow
      </p>
      <div className="flex items-center gap-1 ml-auto">
        {Array.from(selectedApps).slice(0, 5).map(app => (
          <AppIcon key={app} name={app} size={18} className="opacity-70" />
        ))}
        {selectedApps.size > 5 && (
          <span className="text-[10px] text-[#7D8896] ml-1">+{selectedApps.size - 5}</span>
        )}
      </div>
    </div>

    <div className="flex items-center justify-between">
      <button onClick={onBack} data-testid="workflow-back-btn" className="flex items-center gap-1.5 text-sm text-[#7D8896] hover:text-[#F0F4F8] transition-colors">
        <CaretLeft size={14} /> Back
      </button>
      <button onClick={onActivate} data-testid="activate-worker-btn"
        className="px-6 py-2.5 rounded-lg bg-[#00E5C0] hover:bg-[#00D4B0] text-[#0B0F14] text-sm font-semibold transition-colors flex items-center gap-1.5">
        Activate worker <Lightning size={14} weight="fill" />
      </button>
    </div>
  </motion.div>
  );
};

/* ─────────────────────────────────────────────── */
/*  STEP 4 — SETUP LOADING                         */
/* ─────────────────────────────────────────────── */
const SetupLoadingStep = ({ message }) => (
  <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }}
    transition={{ duration: 0.4 }} className="w-full flex flex-col items-center justify-center py-16">
    <div className="relative mb-8">
      <div className="w-16 h-16 rounded-2xl bg-[rgba(0,229,192,0.08)] flex items-center justify-center">
        <CircleNotch size={32} className="text-[#00E5C0] animate-spin" />
      </div>
      <motion.div className="absolute -inset-3 rounded-2xl border border-[rgba(0,229,192,0.15)]"
        animate={{ scale: [1, 1.15, 1], opacity: [0.5, 0, 0.5] }}
        transition={{ duration: 2, repeat: Infinity }} />
    </div>
    <h2 className="text-xl font-bold text-[#F0F4F8] mb-3" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
      Setting up your worker...
    </h2>
    <AnimatePresence mode="wait">
      <motion.p key={message} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
        className="text-[#7D8896] text-sm">{message}</motion.p>
    </AnimatePresence>
  </motion.div>
);

/* ─────────────────────────────────────────────── */
/*  STEP 5 — COMPLETION (routes to workflow view)   */
/* ─────────────────────────────────────────────── */
// Outcomes that are purely process-based (no email reply in workflow)
// Most templates include a RESPOND phase, so this is a short exclusion list
const PROCESS_ONLY_OUTCOMES = new Set(['stay-organised']);

const CompletionStep = ({ outcome, workerId, agentName }) => {
  const navigate = useNavigate();
  const detailUrl = `/workers/${workerId}/detail`;
  const isProcessOnly = PROCESS_ONLY_OUTCOMES.has(outcome?.id);

  return (
    <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.4 }} className="w-full flex flex-col items-center text-center py-8">
      <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }}
        transition={{ type: 'spring', stiffness: 200, delay: 0.1 }}
        className="w-16 h-16 rounded-2xl bg-[rgba(0,229,192,0.12)] border border-[rgba(0,229,192,0.2)] flex items-center justify-center mb-6">
        <CheckCircle size={36} weight="fill" className="text-[#00E5C0]" />
      </motion.div>

      <p className="text-xs text-[#00E5C0] font-medium mb-2 uppercase tracking-[0.08em]">Custom-built for your business</p>
      <h1 className="text-2xl sm:text-3xl font-bold text-[#F0F4F8] mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
        Your worker is ready
      </h1>
      <p className="text-[#7D8896] text-sm mb-2 max-w-sm">
        {agentName || outcome?.label} has been set up with your selected tools.
      </p>

      {isProcessOnly ? (
        <>
          <p className="text-[#5C6778] text-xs mb-8">Review the workflow and configure it for your business.</p>
          <button data-testid="completion-go-to-worker-btn" onClick={() => navigate(detailUrl)}
            className="px-7 py-3.5 rounded-xl bg-[#00E5C0] hover:bg-[#00D4B0] text-[#0B0F14] text-sm font-bold transition-colors flex items-center gap-2 shadow-lg shadow-[rgba(0,229,192,0.12)] mb-3">
            <ArrowRight size={16} weight="bold" /> Go to worker
          </button>
        </>
      ) : (
        <>
          <p className="text-[#5C6778] text-xs mb-8">See it handle a real enquiry. No setup needed.</p>
          <button data-testid="completion-test-enquiry-btn" onClick={() => navigate(detailUrl, { state: { openTestEnquiry: true } })}
            className="px-7 py-3.5 rounded-xl bg-[#00E5C0] hover:bg-[#00D4B0] text-[#0B0F14] text-sm font-bold transition-colors flex items-center gap-2 shadow-lg shadow-[rgba(0,229,192,0.12)] mb-3">
            <Play size={16} weight="fill" /> Run a test enquiry
          </button>
          <button data-testid="review-finish-btn" onClick={() => navigate(detailUrl)}
            className="text-xs text-[#7D8896] hover:text-[#B0B8C4] transition-colors flex items-center gap-1">
            Skip, review workflow <ArrowRight size={10} />
          </button>
        </>
      )}
    </motion.div>
  );
};

/* ─────────────────────────────────────────────── */
/*  MAIN FLOW                                       */
/* ─────────────────────────────────────────────── */
export default function WorkerSetupFlow() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { activeOrganization } = useOrganization();
  const orgId = activeOrganization?.organization_id;
  const incomingTemplateId = searchParams.get('templateId');

  const [step, setStep] = useState(1);
  const [selectedOutcome, setSelectedOutcome] = useState(null);
  const [allTemplates, setAllTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [suggestedTools, setSuggestedTools] = useState([]);
  const [selectedTools, setSelectedTools] = useState(new Set());
  const [toolSearch, setToolSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [totalApps, setTotalApps] = useState(0);
  const [workflowSteps, setWorkflowSteps] = useState([]);
  const [setupMsg, setSetupMsg] = useState(SETUP_MESSAGES[0]);
  const [createdAgent, setCreatedAgent] = useState(null);
  const [error, setError] = useState('');
  const cancelRef = useRef(false);
  const [industryTools, setIndustryTools] = useState([]);
  const [detectedIndustry, setDetectedIndustry] = useState(null);
  const [stackTools, setStackTools] = useState([]);
  const [toolRoleMapping, setToolRoleMapping] = useState([]);
  const [showingConfirmation, setShowingConfirmation] = useState(false);

  // Fetch the org's tool stack (previously declared tools) — pre-populate step 1
  useEffect(() => {
    if (!orgId) return;
    (async () => {
      try {
        const res = await fetchWithOrg(`${BACKEND_URL}/api/service-os/tool-stack`, {}, orgId);
        if (res.ok) {
          const data = await res.json();
          const names = (data.tools || []).map(t => t.name).filter(n => !isTestTool(n));
          setStackTools(names);
          // Pre-select tools from onboarding
          if (names.length > 0) {
            setSelectedTools(new Set(names));
          }
        }
      } catch {}
    })();
  }, [orgId]);

  // Fetch org business context to infer industry for smart tool suggestions
  useEffect(() => {
    if (!orgId) return;
    (async () => {
      try {
        // Try fetching an existing automation's behavior to get business context
        const res = await fetchWithOrg(`${BACKEND_URL}/api/service-os/automations?limit=1`, {}, orgId);
        if (res.ok) {
          const data = await res.json();
          const auto = (data.automations || [])[0];
          const ctx = auto?.behavior?.business_context || auto?.behavior?.website_url || '';
          if (ctx) {
            const result = inferIndustry(ctx);
            setIndustryTools(result.tools || []);
            setDetectedIndustry(result.label || null);
            return;
          }
        }
        // Fallback: try org name
        if (activeOrganization?.name) {
          const result = inferIndustry(activeOrganization.name);
          if (result.industry !== 'general') {
            setIndustryTools(result.tools || []);
            setDetectedIndustry(result.label || null);
          }
        }
      } catch {}
    })();
  }, [orgId, activeOrganization?.name]);

  // Fetch all templates on mount
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/public/automations?limit=100`);
        if (res.ok) {
          const data = await res.json();
          setAllTemplates(data.templates || []);
        }
      } catch { /* silent */ }
    })();
  }, []);

  // Build suggested tools for step 1 (industry + defaults — excludes already-selected)
  useEffect(() => {
    const defaults = ['Gmail', 'Google Calendar', 'Google Sheets', 'Slack', 'HubSpot', 'Xero', 'QuickBooks', 'Stripe'];
    const unique = [...new Set([...industryTools, ...defaults])];
    setSuggestedTools(unique);
  }, [industryTools]);

  // Auto-select outcome when arriving from marketplace with templateId
  useEffect(() => {
    if (!incomingTemplateId || !allTemplates.length || selectedOutcome) return;
    // Find the outcome that matches this template
    const matchingOutcome = OUTCOMES.find(o => o.templateId === incomingTemplateId);
    if (matchingOutcome) {
      handleOutcomeSelect(matchingOutcome);
      return;
    }
    // Also check if any template in DB matches the ID, and match by name/category
    const tmpl = allTemplates.find(t => t.id === incomingTemplateId);
    if (tmpl) {
      const byCategory = OUTCOMES.find(o =>
        o.category.toLowerCase() === (tmpl.outcome_category || '').toLowerCase()
      );
      if (byCategory) {
        handleOutcomeSelect(byCategory);
        return;
      }
      // Fallback: use the first outcome but set this template
      setSelectedTemplate(tmpl);
      const tools = tmpl.works_with || [];
      const defaults = ['Gmail', 'Google Calendar'];
      const unique = [...new Set([...tools, ...defaults])];
      setSuggestedTools(unique);
      setSelectedTools(new Set(unique.slice(0, Math.min(3, unique.length))));
      setSelectedOutcome(OUTCOMES[0]); // Default to first outcome
      setStep(3); // Skip to workflow step since we already have tools + outcome
    }
  }, [incomingTemplateId, allTemplates]); // eslint-disable-line

  // Debounced search against the full integration catalog
  const searchTimerRef = useRef(null);
  const handleSearchChange = useCallback((query) => {
    setToolSearch(query);
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    if (!query.trim()) {
      setSearchResults([]);
      setSearchLoading(false);
      return;
    }
    // Instant local results while API loads
    const q = query.trim().toLowerCase();
    const localMatches = LOCAL_APP_CATALOG
      .filter(name => name.toLowerCase().includes(q))
      .map(name => ({ name, slug: name.toLowerCase().replace(/\s+/g, ''), logo: getAppLogo(name) }));
    setSearchResults(localMatches.slice(0, 20));
    setSearchLoading(true);

    // Debounced API search for the full 1000+ catalog
    searchTimerRef.current = setTimeout(async () => {
      try {
        const res = await fetchWithOrg(
          `${BACKEND_URL}/api/service-os/integrations/apps?q=${encodeURIComponent(query.trim())}`,
          {},
          orgId
        );
        if (res.ok) {
          const data = await res.json();
          const apiApps = (data.apps || []).slice(0, 30);
          if (data.total_apps) setTotalApps(data.total_apps);
          // Merge: API results first (have richer data), then local fallbacks not in API results
          const apiSlugs = new Set(apiApps.map(a => a.slug));
          const merged = [
            ...apiApps,
            ...localMatches.filter(l => !apiSlugs.has(l.slug)),
          ].slice(0, 30);
          setSearchResults(merged);
        }
        // If API fails (401 etc.), local results stay visible — no action needed
      } catch {
        // Local results already set above — no action needed
      } finally {
        setSearchLoading(false);
      }
    }, 300);
  }, [orgId]);

  // When outcome is selected → pick default template (tools are already selected from step 1)
  useEffect(() => {
    if (!selectedOutcome || !allTemplates.length) return;
    const defaultTmpl = allTemplates.find(t => t.id === selectedOutcome.templateId);
    setSelectedTemplate(defaultTmpl || null);
  }, [selectedOutcome, allTemplates]);

  // Handlers — Step 1 is now tools, Step 2 is outcomes
  const handleToolsContinue = () => {
    setStep(2);
  };

  const handleOutcomeSelect = (outcome) => {
    setSelectedOutcome(outcome);
    setWorkflowSteps([]);
    setError('');
    // Go directly to build workflow (step 3)
    handleBuildWorkflowForOutcome(outcome);
  };

  const handleToolToggle = (tool) => {
    setSelectedTools(prev => {
      const next = new Set(prev);
      next.has(tool) ? next.delete(tool) : next.add(tool);
      return next;
    });
  };

  // Generate workflow when outcome is selected (from step 2)
  const handleBuildWorkflowForOutcome = (outcome) => {
    if (!outcome) return;
    const mapping = getToolRoleMapping(selectedTools);
    setToolRoleMapping(mapping);
    setShowingConfirmation(true);
    setStep(3);
  };

  // After tool confirmation animation completes, build and show workflow
  const handleConfirmationDone = useCallback(() => {
    const steps = generateWorkflow(selectedOutcome.id, selectedTools);
    setWorkflowSteps(steps);
    setShowingConfirmation(false);
  }, [selectedOutcome, selectedTools]);

  // Update a step in the workflow (from settings panel)
  const handleUpdateStep = useCallback((index, updatedStep) => {
    setWorkflowSteps(prev => {
      const next = [...prev];
      next[index] = updatedStep;
      return next;
    });
  }, []);

  // ── Core: create worker from template + dynamic workflow ──
  const startSetup = useCallback(async () => {
    const templateId = selectedTemplate?.id || selectedOutcome?.templateId;
    if (!templateId || !orgId) return;
    cancelRef.current = false;

    let msgIndex = 0;
    const interval = setInterval(() => {
      msgIndex = Math.min(msgIndex + 1, SETUP_MESSAGES.length - 1);
      setSetupMsg(SETUP_MESSAGES[msgIndex]);
    }, 1200);

    const tryInstantiate = async (attempt = 1) => {
      const res = await fetchWithOrg(
        `${BACKEND_URL}/api/agent-templates/${templateId}/instantiate`,
        { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ tool_preferences: Array.from(selectedTools) }) },
        orgId
      );
      if (!res.ok) {
        if (attempt < 2) return tryInstantiate(attempt + 1);
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Setup failed');
      }
      const data = await res.json();
      if (!data.agent_id) {
        if (attempt < 2) return tryInstantiate(attempt + 1);
        throw new Error('Invalid response');
      }

      const check = await fetchWithOrg(`${BACKEND_URL}/api/agents/${data.agent_id}`, {}, orgId);
      if (!check.ok && attempt < 2) return tryInstantiate(attempt + 1);

      return data;
    };

    try {
      const data = await tryInstantiate();
      clearInterval(interval);
      if (cancelRef.current) return;

      if (data.requires_wizard) {
        navigate(`/partner-pro/ai-workforce/agents/${data.agent_id}/wizard`);
        return;
      }

      // Create a service-OS automation with the DYNAMIC workflow steps
      const workerName = selectedTemplate?.name || selectedOutcome?.label || 'AI Worker';
      const automationSteps = workflowSteps.map(s => ({
        type: s.type === 'trigger' ? 'trigger' : 'action',
        label: s.label,
        app: s.app || null,
      }));

      const autoRes = await fetchWithOrg(
        `${BACKEND_URL}/api/service-os/automations`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            name: workerName,
            automation_type: 'template',
            steps: automationSteps,
            status: 'draft',
            requires_review: true,
            tool_preferences: Array.from(selectedTools),
            outcome_id: selectedOutcome?.id,
            agent_id: data.agent_id,
          }),
        },
        orgId
      );
      let workerId = data.agent_id;
      if (autoRes.ok) {
        const autoData = await autoRes.json();
        workerId = autoData.automation?.id || data.agent_id;
      }

      setCreatedAgent({ id: workerId, agentId: data.agent_id, name: workerName });

      // Sync selected tools back to the org's tool stack (additive)
      try {
        await fetchWithOrg(`${BACKEND_URL}/api/service-os/tool-stack/sync`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ tools: Array.from(selectedTools) }),
        }, orgId);
      } catch {} // Non-blocking

      await new Promise(r => setTimeout(r, 500));
      if (!cancelRef.current) setStep(5);
    } catch (err) {
      clearInterval(interval);
      if (!cancelRef.current) {
        // Fallback: redirect to Worker Builder with context pre-filled
        const workerName = selectedOutcome?.label || 'AI Worker';
        const toolsList = Array.from(selectedTools).join(', ');
        const builderUrl = `/workers/new?type=event&name=${encodeURIComponent(workerName)}&tools=${encodeURIComponent(toolsList)}`;
        navigate(builderUrl);
      }
    }
  }, [selectedOutcome, selectedTemplate, orgId, selectedTools, workflowSteps, navigate]);

  const handleActivate = () => {
    setStep(4);
    startSetup();
  };

  return (
    <div className="min-h-screen bg-[#161B26] flex flex-col" data-testid="worker-setup-flow">
      {/* Top bar */}
      <header className="flex items-center justify-between px-6 py-4 border-b border-[rgba(255,255,255,0.04)]">
        <button onClick={() => navigate('/dashboard')} data-testid="setup-close-btn"
          className="flex items-center gap-1.5 text-sm text-[#7D8896] hover:text-[#F0F4F8] transition-colors">
          <X size={16} /> Close
        </button>
        <StepIndicator current={step} total={TOTAL_STEPS} />
        <div className="w-16" />
      </header>

      {/* Main content */}
      <main className="flex-1 flex items-start justify-center px-6 py-8 overflow-y-auto">
        <div className="w-full max-w-xl">
          {error && (
            <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }}
              className="mb-6 px-4 py-3 rounded-lg bg-[rgba(239,68,68,0.08)] border border-[rgba(239,68,68,0.2)] text-sm text-red-400 flex items-center gap-2"
              data-testid="setup-error">
              <X size={14} /> {error}
              <button onClick={() => setError('')} className="ml-auto text-red-400/60 hover:text-red-400"><X size={12} /></button>
            </motion.div>
          )}

          <AnimatePresence mode="wait">
            {step === 1 && (
              <ToolsFirstStep key="s1"
                suggested={suggestedTools}
                selected={selectedTools}
                searchQuery={toolSearch}
                searchResults={searchResults}
                searchLoading={searchLoading}
                totalApps={totalApps}
                onToggle={handleToolToggle}
                onSearchChange={handleSearchChange}
                onContinue={handleToolsContinue}
                detectedIndustry={detectedIndustry} />
            )}

            {step === 2 && (
              <SmartOutcomeStep key="s2"
                selectedTools={selectedTools}
                onSelect={handleOutcomeSelect}
                onCustom={() => navigate('/command')}
                onBack={() => setStep(1)} />
            )}

            {step === 3 && showingConfirmation && (
              <ToolConfirmationStep key="s3a"
                toolMapping={toolRoleMapping}
                onDone={handleConfirmationDone} />
            )}

            {step === 3 && !showingConfirmation && workflowSteps.length > 0 && (
              <WorkflowPreviewStep key="s3"
                outcome={selectedOutcome}
                workflowSteps={workflowSteps}
                selectedApps={selectedTools}
                onActivate={handleActivate}
                onBack={() => { setSelectedOutcome(null); setStep(2); }}
                onUpdateStep={handleUpdateStep}
                connectedApps={new Set()} />
            )}

            {step === 4 && <SetupLoadingStep key="s4" message={setupMsg} />}

            {step === 5 && (
              <CompletionStep key="s5"
                outcome={selectedOutcome}
                workerId={createdAgent?.id}
                agentName={createdAgent?.name} />
            )}
          </AnimatePresence>
        </div>
      </main>
    </div>
  );
}
